// src/pages/AuthCallback.js

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function AuthCallback() {
  const navigate            = useNavigate()
  const [params]            = useSearchParams()
  const [status, setStatus] = useState('Signing you in…')
  const ran                 = useRef(false)

  useEffect(() => {
    if (ran.current) return
    ran.current = true

    const publicToken      = params.get('triage_session_id')
    const calculatorToken  = params.get('calculator_token')
    const next             = params.get('next')

    async function claimAndNavigate(session) {
      // Fetch user_type for post-login routing
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', session.user.id)
        .single()
      const userType = profile?.user_type ?? 'consumer'

      // ── Calculator session claim ─────────────────────────────────────────
      // Delegates to the generalized claim-session function (replaces the
      // inline subject-creation logic that used to live here; one endpoint
      // for both products, the old triage-claim function is retired).
      if (calculatorToken) {
        setStatus('Loading your estimate…')
        try {
          const res = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/claim-session`,
            {
              method:  'POST',
              headers: {
                'Content-Type':  'application/json',
                'Authorization': `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ product: 'calculator', public_token: calculatorToken }),
            }
          )
          const claimData = await res.json().catch(() => ({}))
          if (claimData.tax_subject_id) {
            sessionStorage.setItem('active_subject_id', claimData.tax_subject_id)
          }
        } catch (err) {
          console.error('calculator claim-session error:', err)
          // Non-fatal — still navigate to the result
        }
        navigate(`/calculator/result/${calculatorToken}`, { replace: true })
        return
      }

      if (publicToken) {
        setStatus('Saving your snapshot…')
        try {
          const res = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/claim-session`,
            {
              method:  'POST',
              headers: {
                'Content-Type':  'application/json',
                'Authorization': `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ product: 'triage', public_token: publicToken }),
            }
          )
          // Pre-seed sessionStorage so useSubjects initializes with the
          // correct activeSubjectId immediately — no async wait on ProfilePage
          const claimData = await res.json().catch(() => ({}))
          if (claimData.tax_subject_id) {
            sessionStorage.setItem('active_subject_id', claimData.tax_subject_id)
          }
        } catch (err) {
          console.error('triage claim-session error:', err)
        }

        // Marketing-consent capture used to live here, keyed off a
        // ?marketing_consent= URL param — removed. Nothing that builds
        // this redirect URL (TriagePage.js, TriageResults.js) ever
        // appended that param, so this block always wrote `false`
        // regardless of anyone's actual preference. The real, live
        // marketing-consent capture is AdvisorConnectForm.js's checkbox →
        // advisor-connect/index.ts → consent_events (subject_id-keyed,
        // not session/table-column-keyed — see consentEvents.ts).

        navigate(`/spainsnapshot/results/${publicToken}`, { replace: true })
      } else if (next) {
        navigate(next, { replace: true })
      } else if (userType === 'partner') {
        navigate('/advisor', { replace: true })
      } else {
        navigate('/profile', { replace: true })
      }
    }

    // Check for existing session first (same-device magic link click)
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        await claimAndNavigate(session)
        return
      }

      // No session yet — wait for SIGNED_IN event (cross-device or delayed)
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (event !== 'SIGNED_IN' || !session) return
          subscription.unsubscribe()
          await claimAndNavigate(session)
        }
      )
    })
  }, [])

  return (
    <div className="asmt-state">
      <div className="app-spinner" />
      <p style={{ marginTop: '1rem', fontSize: 'var(--text-sm)', color: 'var(--muted)' }}>
        {status}
      </p>
    </div>
  )
}